import { useEffect, useState } from "react";
import { GitCompare, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : typeof v === "object" ? `${v.label} · ${v.type}${v.value ? ` · ${v.value}` : ""}` : String(v));
const TONE = { added: "bg-emerald-50 text-emerald-800 border-emerald-200", removed: "bg-rose-50 text-rose-800 border-rose-200", changed: "bg-amber-50 text-amber-900 border-amber-200" };

export function DiffList({ diff }) {
  if (!diff) return <div className="text-sm text-slate-500">Comparing…</div>;
  return (
    <div className="space-y-3" data-testid="rules-diff">
      <div className="grid grid-cols-2 gap-3 text-xs"><div className="border rounded p-2 bg-slate-50" data-testid="diff-from"><div className="eyebrow">Old · {diff.from.label}</div>{diff.from.saved_by}</div><div className="border rounded p-2 bg-slate-50" data-testid="diff-to"><div className="eyebrow">New · {diff.to.label}</div>{diff.to.saved_by}</div></div>
      {diff.changes.length === 0 ? <div className="text-sm text-slate-600" data-testid="diff-none">No differences — these versions are identical.</div> : (
        <table className="w-full text-sm" data-testid="diff-table"><thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Field</th><th>Change</th><th>Old</th><th>New</th></tr></thead>
          <tbody>{diff.changes.map((c) => (
            <tr key={c.path} className="border-t border-slate-100" data-testid={`diff-row-${c.path}`}>
              <td className="py-1.5 pr-2">{c.label}</td>
              <td><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${TONE[c.kind]}`} data-testid={`diff-kind-${c.path}`}>{c.kind}</span></td>
              <td className={`pr-2 ${c.kind !== "added" ? "line-through text-rose-700" : "text-slate-400"}`}>{fmt(c.old)}</td>
              <td className={c.kind !== "removed" ? "text-emerald-800 font-medium" : "text-slate-400"}>{fmt(c.new)}</td>
            </tr>))}</tbody></table>)}
    </div>
  );
}

export function RulesDiffDialog({ pa, fromRef, toRef = "current", onClose, onRestore }) {
  const [diff, setDiff] = useState(null);
  useEffect(() => { setDiff(null); api.get(`/intake/rules/${pa}/diff`, { params: { from_ref: fromRef, to_ref: toRef } }).then((r) => setDiff(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [pa, fromRef, toRef]); // eslint-disable-line
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rules-diff-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitCompare className="h-4 w-4" />{onRestore ? "What restoring this version would change" : "Compare rule versions"}</DialogTitle></DialogHeader>
        <DiffList diff={diff} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="diff-close">Close</Button>
          {onRestore && <Button className="bg-blue-900" disabled={!diff || diff.changes.length === 0} onClick={onRestore} data-testid="diff-confirm-restore"><RotateCcw className="h-4 w-4 mr-1" />Restore this version</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
